import React, { useState, useEffect } from "react";
import Footer from "./Footer";
import { CircleCheck, Camera, CheckCircle } from "lucide-react";
import { Hourglass } from "react-loader-spinner";
import useStickyButton from "../hooks/useStickyButton";
import isMobileDevice from "../../../utils/isMobileDevice";
import DesktopQrModal from "../../../components/DesktopQrModal";
import SecureCamera from "../../../components/SecureCamera/SecureCamera";

const CheckQualityHologramDetection = (props) => {
  const { isSubmitting, imageBuffer, onRetake, onConfirm } = props;

  const global = {};
  const textColor = global?.text?.fontColor || "#6b7280"; // default gray-500
  const highlightTextColor = global?.highlightText?.fontColor || "#2563eb"; // blue-600
  const buttonBgColor = global?.button?.backgroundColor || "#2563eb"; // blue-600
  const retakeButtonTextColor = global?.button?.fontColor || "#3b82f6"; // blue-500
  const buttonTextColor = global?.button?.fontColor || "#ffffff"; // white
  const pageBgColor = global?.pageContainer?.backgroundColor || "#ffffff"; // white
  const cardBgColor = global?.card?.backgroundColor || "#f3f4f6"; // gray-50
  const cardOpacity = global?.card?.opacity || "1"; // default opacity

  const { buttonContainerRef, sentinelRef, isSticky, buttonHeight } = useStickyButton();

  const [imageBlob, setImageBlob] = useState();
  const [showDesktopNotice, setShowDesktopNotice] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  const handleRetake = () => {
    if (!isMobileDevice()) {
      setShowDesktopNotice(true);
      return;
    }

    setShowDesktopNotice(false);
    setShowCamera(true);
  };

  const handleSecureCapture = ({ file }) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = async () => {
      onRetake(reader.result);
    };
  };

  useEffect(() => {
    if (imageBuffer) {
      setImageBlob(new Blob([imageBuffer], { type: "image/jpeg" }));
    }
  }, [imageBuffer]);

  return (
    <div
      style={{
        background: pageBgColor,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        padding: "1rem",
        height: "100%",
      }}
    >
      <h2
        style={{
          color: textColor,
          fontSize: "1.25rem",
          fontWeight: "bold",
          marginBottom: "1rem",
          textAlign: "center",
        }}
      >
        Review the
        <span style={{ color: highlightTextColor }}> Hologram</span> <br />
        on your <span style={{ color: highlightTextColor }}>MyKad</span>
      </h2>

      {/* Content area with Apple-style spacing */}
      <div>
        {/* Image container with soft shadow and rounded corners */}
        <div className="flex flex-col items-center mt-4 mb-4">
          {imageBlob ? (
            <img
              src={URL.createObjectURL(imageBlob)}
              className="h-60 md:h-64 lg:h-80 w-auto rounded-lg shadow-md shadow-black"
            />
          ) : null}
          <p
            style={{
              color: textColor,
              textAlign: "center",
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
              fontWeight: "bold",
            }}
          >
            MyKad With Hologram
          </p>
        </div>

        {/* Requirements section with iOS-style icons */}
        <div
          style={{
            backgroundColor: cardBgColor,
            borderRadius: "0.75rem",
            padding: "1rem",
            marginBottom: "1.5rem",
            opacity: cardOpacity,
          }}
        >
          <p
            style={{
              fontSize: "0.875rem",
              marginBottom: "0.75rem",
              fontWeight: "500", // font-medium
              color: textColor, // text-gray-500
            }}
          >
            PLEASE ENSURE:
          </p>
          <div className="space-y-4 mt-4">
            <ul className="space-y-2">
              <li className="flex items-center space-x-2">
                <CheckCircle className="text-green-500" />
                <span style={{ color: textColor }}>
                  The photo is clear and sharp
                </span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="text-green-500" />
                <span style={{ color: textColor }}>
                  All corners of your MyKad are visible
                </span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="text-green-500" />
                <span style={{ color: textColor }}>
                  Hologram is clearly visible
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex flex-1" />

      {/* Sentinel element for Intersection Observer */}
      <div ref={sentinelRef} style={{ height: "1px", visibility: "hidden" }} />

      {/* Spacer to maintain space when button is sticky */}
      {isSticky && buttonHeight > 0 && (
        <div style={{ height: `${buttonHeight}px`, width: "100%" }} />
      )}

      {/* Action buttons area */}
      {isSubmitting ? (
        <div className="flex flex-col justify-center items-center mb-6">
          <Hourglass height={"32"} width={"32"} color="#1E40AF" />
          <p style={{ color: textColor, marginTop: "2rem" }}>
            Please wait while we upload your photo...
          </p>
        </div>
      ) : (
        <div 
          ref={buttonContainerRef}
          className="px-6 mb-6"
          style={{
            position: "relative",
            bottom: "auto",
            left: "auto",
            right: "auto",
            padding: "0 1.5rem",
            background: "transparent",
            zIndex: "auto",
            transition: "all 0.3s ease-in-out",
            pointerEvents: "auto",
          }}
        >
          <button
            style={{
              background: buttonBgColor,
              color: buttonTextColor,
              width: "66.666667%",
              padding: "0.75rem 1rem",
              borderRadius: "9999px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              margin: "0 auto",
              transition: "transform 0.15s ease-in-out",
              pointerEvents: "auto",
            }}
            onClick={() => onConfirm(imageBlob)}
            disabled={isSubmitting}
            onMouseDown={(e) => !e.currentTarget.disabled && (e.currentTarget.style.transform = 'scale(0.95)')}
            onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            onTouchStart={(e) => !e.currentTarget.disabled && (e.currentTarget.style.transform = 'scale(0.95)')}
            onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            Confirm
            <CircleCheck className="ml-2" />
          </button>
          <button
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              paddingTop: "0.75rem", // py-3
              paddingBottom: "0.75rem",
              fontWeight: 500, // font-medium
              fontSize: "1rem", // text-base
              marginTop: "0.5rem", // mt-2
              transition: "color 0.15s ease-in-out",
              color: retakeButtonTextColor, // text-blue-500
              pointerEvents: "auto",
            }}
            onClick={handleRetake}
            onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
            onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
            onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            Retake
            <Camera className="ml-2" />
          </button>
        </div>
      )}

      <DesktopQrModal
        open={showDesktopNotice}
        onClose={() => setShowDesktopNotice(false)}
      />

      <SecureCamera
        isOpen={showCamera}
        onClose={() => setShowCamera(false)}
        onCapture={handleSecureCapture}
        frameType="ID"
        title="Place your MyKad within the frame, hologram visible"
        theme={{
          primaryColor: buttonBgColor,
          textColor: "#ffffff",
        }}
      />

      <Footer
        style={{
          fontSize: "0.75rem",
          color: textColor,
          marginTop: "1rem",
        }}
      />
    </div>
  );
};

export default CheckQualityHologramDetection;
